import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Alert,
  darken,
  IconButton,
  lighten,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material'
import { AlertPhone, Device, User, UserAlertRule } from '../types'
import { THEMES } from '../constants'
import { ExpandMore } from '@mui/icons-material'
import AlertRuleDataGrid from './AlertRuleDataGrid'
import useAccordion from '../hooks/useAccordion'
import useDeviceAlertTemplates from '../hooks/useDeviceAlertTemplates'
import useValveAlertTemplates from '../hooks/useValveAlertTemplates'
import { useEffect, useState } from 'react'
import { AlertGroup } from '../types/Alerts'
import { AxiosRequestConfig } from 'axios'
import LoadingState from './LoadingState'
import AlertRuleAddButton from './AlertRuleAddButton'
import AlertTemplateDefaultsButton from './AlertTemplateDefaultsButton'
import { useTranslation } from 'react-i18next'
import { getUserAlertRules } from '../services/userService'
import AlertPhoneAlertRulesDataGrid from './alertPhone/AlertPhoneAlertRulesDataGrid'
import DeviceStatusIndicator from './DeviceStatusIndicator'
import { getDeviceAlertRules } from '../services/deviceService'
import { DeviceType } from '../enums'
import { getValveAlertRules } from '../services/valve'
import { datadogRum } from '@datadog/browser-rum'

interface DeviceAlertRuleAccordionProps extends Omit<AccordionProps, 'children'> {
  device: Device
  user?: User | null
  alertPhone?: AlertPhone | null
  alertPhoneAlertRules?: UserAlertRule[]
}

export default function DeviceAlertRuleAccordion({
  device,
  user,
  alertPhone,
  alertPhoneAlertRules,
  sx,
  ...props
}: DeviceAlertRuleAccordionProps) {
  // TODO: Refactor so that this takes just User | AlertPhone so we don't need this check
  const isInvalid = !user && !alertPhone

  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The theme function.
   */
  const theme = useTheme()

  /**
   * The accordion state.
   */
  const { expanded, toggleAccordion } = useAccordion()

  /**
   * The device alert templates.
   */
  const deviceAlertTemplates = useDeviceAlertTemplates()

  /**
   * The valve alert templates.
   */
  const valveAlertTemplates = useValveAlertTemplates()

  /**
   * The alert templates.
   */
  const alertTemplates = makeAlertTemplates()

  /**
   * Make the alert templates.
   */
  function makeAlertTemplates() {
    if (device.type === DeviceType.VALVE) {
      return [...deviceAlertTemplates, ...valveAlertTemplates]
    } else {
      return deviceAlertTemplates
    }
  }

  /**
   * The alert rules.
   */
  const [alertRules, setAlertRules] = useState<AlertGroup[]>([])

  /**
   * The user alert rules state.
   */
  const [userAlertRules, setUserAlertRules] = useState<UserAlertRule[]>([])

  /**
   * The enabled alert rules.
   */
  const enabledAlertRules = alertRules.filter((group) =>
    group.alerts.some((alert) => alert.enabled)
  )

  /**
   * The loading state.
   */
  const [isLoading, setLoading] = useState<boolean>(true)

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>()

  /**
   * Load the state for the user.
   */
  async function loadStateForUser(userId: number, deviceName: string, config?: AxiosRequestConfig) {
    try {
      setLoading(true)
      await Promise.all([
        loadAlertRules(deviceName, config),
        loadUserAlertRules(userId, deviceName, config)
      ])
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the state for the alert phone.
   */
  async function loadStateForAlertPhone(
    alertPhoneId: number,
    deviceName: string,
    config?: AxiosRequestConfig
  ) {
    try {
      setLoading(true)
      await loadAlertRules(deviceName, config)
      setUserAlertRules(
        alertPhoneAlertRules?.filter(
          (rule) => rule.alertPhoneId === alertPhoneId && rule.deviceName === deviceName
        ) ?? []
      )
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the alert rules.
   */
  async function loadAlertRules(name: string, config?: AxiosRequestConfig) {
    if (device.type === DeviceType.VALVE) {
      const [deviceAlertRules, valveAlertRules] = await Promise.all([
        getDeviceAlertRules(name, config),
        getValveAlertRules(name, config)
      ])

      setAlertRules([...deviceAlertRules, ...valveAlertRules])
    } else {
      setAlertRules(await getDeviceAlertRules(name, config))
    }
  }

  /**
   * Load the user alert rules.
   */
  async function loadUserAlertRules(userId: number, name: string, config?: AxiosRequestConfig) {
    // setUserAlertRules(await getUserAlertRulesByDevice(userId, name, config))
    const userAlertRules = (await getUserAlertRules(userId, {}, config)).filter((rule) =>
      device?.asset?.projectId
        ? rule.deviceName === name || rule.projectId === device.asset.projectId
        : rule.deviceName === name
    )
    setUserAlertRules(userAlertRules)
  }

  /**
   * Load the state when the ruuvitag or the current user changes.
   */
  useEffect(() => {
    if (device?.name) {
      const controller = new AbortController()

      if (user?.id) {
        loadStateForUser(user.id, device.name, { signal: controller.signal })
      } else if (alertPhone?.id) {
        loadStateForAlertPhone(alertPhone.id, device.name, { signal: controller.signal })
      }

      return () => controller.abort()
    }
  }, [device.name, user?.id, alertPhone?.id])

  useEffect(() => {
    if (isInvalid) {
      datadogRum.addError(
        new Error('DeviceAlertRuleAccordion requires either "user" or "alertPhone" prop.')
      )
    }
  }, [isInvalid])

  if (isInvalid) {
    return null
  }

  return (
    <Accordion
      expanded={expanded}
      sx={{
        ...sx,
        width: '100%',
        boxShadow: 0,
        '::before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          pl: 4,
          pr: 2,
          cursor: 'default',
          ':hover': {
            backgroundColor:
              theme.palette.mode === THEMES.DARK
                ? lighten(theme.palette.background.paper, 0.04)
                : darken(theme.palette.background.paper, 0.04)
          }
        }
      }}
      {...props}
    >
      <AccordionSummary
        onClick={toggleAccordion}
        expandIcon={
          <Tooltip title={expanded ? t('hideAlertRules') : t('showAlertRules')}>
            <IconButton>
              <ExpandMore />
            </IconButton>
          </Tooltip>
        }
      >
        <ListItem
          component="div"
          disablePadding
          sx={{
            p: 0,
            m: 0,
            width: '100%'
          }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <DeviceStatusIndicator key={`${device.name}-${device.status}`} device={device} />
          </ListItemIcon>
          <ListItemText
            sx={{ width: 'auto', pr: 0 }}
            primary={device?.asset?.name ?? device.name}
            secondary={device.name}
            primaryTypographyProps={{
              sx: { order: '1 !important' }
            }}
            secondaryTypographyProps={{
              sx: { order: '2 !important' }
            }}
          />

          {!isLoading && expanded && !alertPhone && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              onClick={(event) => event.stopPropagation()}
              sx={{ mr: 1 }}
            >
              <AlertTemplateDefaultsButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                device={device}
                useRedux={false}
                afterUpdate={setAlertRules}
              />
              <AlertRuleAddButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                device={device}
                useRedux={false}
                afterUpdate={setAlertRules}
              />
            </Stack>
          )}
        </ListItem>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 8, py: 2 }}>
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <Alert severity="error">{t('loadAlertRulesError')}</Alert>
        ) : enabledAlertRules.length === 0 ? (
          <Alert severity="info">{t('noAlertRules')}</Alert>
        ) : alertPhone ? (
          <AlertPhoneAlertRulesDataGrid
            alertPhone={alertPhone}
            device={device}
            alertPhoneAlertRules={userAlertRules}
            alertRules={alertRules}
            onUpdateAlertPhoneAlertRules={setUserAlertRules}
          />
        ) : (
          <AlertRuleDataGrid
            device={device}
            project={device?.asset?.project ?? undefined}
            alertTemplates={alertTemplates}
            alertRules={alertRules}
            userAlertRules={userAlertRules}
            useRedux={false}
            afterUpdate={setAlertRules}
            afterUserAlertRulesUpdate={setUserAlertRules}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}
